import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import {
  AppConfig,
  LAYOUT_CONFIG_DEFAULTS,
  LAYOUT_CONFIG_STORAGE_KEY,
  mergeLayoutConfigWithDefaults,
  writeStoredLayoutConfig,
} from '../layout/service/layout-config-storage';
import { TutorialService } from '../caas/tutorial/tutorial.service';
import { TutorialCompletionState } from '../caas/tutorial/tutorial.model';

// ── Shared interface (mirrors the backend UserPreferences type) ───────────────

/**
 * Combined preferences object persisted to DynamoDB.
 *
 * - `layout` mirrors the `AppConfig` shape from layout-config-storage.ts.
 * - `tutorialCompletions` is a map of `toolId → true` for every tour the user
 *   has completed or skipped.
 *
 * Both fields also continue to be written individually to localStorage as the
 * fast, synchronous read path.  This backend record is the source of truth for
 * cross-device sync only.
 */
export interface UserPreferences {
  layout: AppConfig;
  tutorialCompletions: TutorialCompletionState;
}

// ── localStorage key for tutorial completions ────────────────────────────────

/**
 * Reads all tutorial completion flags from localStorage.
 * Keys match the pattern `centcom:tutorial:{username}:{toolId}` and the value
 * is an epoch-ms timestamp representing when the tour was completed.
 * We collect them into a plain map of `toolId → timestamp` (dropping the
 * username prefix since the backend record is already scoped to one user).
 */
function readTutorialCompletions(username: string): TutorialCompletionState {
  const completions: TutorialCompletionState = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) { 
        continue; 
      }
      
      const value = localStorage.getItem(key);
      const toolId = TutorialService.getToolIdFromStorageKey(username, key);
      if (toolId !== null && TutorialService.isComplete(value)) {
        completions[toolId] = parseInt(value!, 10);
      }
    }
  } catch {
    console.warn('[UserPreferencesService] Unable to read tutorial completions from localStorage.');
  }
  return completions;
}

// ── Service ───────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class UserPreferencesService {
  private readonly endpoint = '/api/preferences';

  constructor(
    private readonly http: HttpClient,
    private readonly msalService: MsalService,
  ) {}

  // ── Username helper ─────────────────────────────────────────────────────────

  private getUsername(): string {
    try {
      return (
        this.msalService.instance.getActiveAccount()?.username ?? 'anonymous'
      );
    } catch {
      return 'anonymous';
    }
  }

  // ── Build / apply ───────────────────────────────────────────────────────────

  /**
   * Reads both localStorage sources and assembles the current `UserPreferences`
   * snapshot to be sent to the backend.
   */
  buildCurrentPreferences(): UserPreferences {
    const username = this.getUsername();

    // Layout config — read directly from localStorage, fall back to defaults.
    let layout: AppConfig;
    try {
      const raw = localStorage.getItem(LAYOUT_CONFIG_STORAGE_KEY);
      const stored = raw ? (JSON.parse(raw) as Partial<AppConfig>) : null;
      layout = mergeLayoutConfigWithDefaults(LAYOUT_CONFIG_DEFAULTS, stored);
    } catch {
      layout = { ...LAYOUT_CONFIG_DEFAULTS };
    }

    return {
      layout,
      tutorialCompletions: readTutorialCompletions(username),
    };
  }

  /**
   * Applies a `UserPreferences` record received from the backend into
   * localStorage.  This is called once on login so that the rest of the app
   * (LayoutService, TutorialService) continues to read from localStorage
   * without any changes.
   */
  applyPreferences(prefs: UserPreferences): void {
    const username = this.getUsername();

    // Apply layout config to localStorage.
    try {
      writeStoredLayoutConfig(prefs.layout);
    } catch {
      // fail silently
    }

    // Apply tutorial completions to localStorage.
    TutorialService.writeTutorialCompletions(username, prefs.tutorialCompletions ?? {});
  }

  // ── Backend sync ────────────────────────────────────────────────────────────

  /**
   * Fetches the persisted preferences from the backend and applies them to
   * localStorage.  Call this once after the user authenticates so that
   * cross-device changes are reflected.
   *
   * If the server returns an empty record (first-time user) the local state
   * is left unchanged.
   */
  loadFromBackend(): Observable<UserPreferences | Record<string, never>> {
    return this.http
      .get<UserPreferences | Record<string, never>>(this.endpoint)
      .pipe(
        tap((prefs) => {
          // Only apply if the response looks like a real preferences record.
          if (prefs && 'layout' in prefs) {
            this.applyPreferences(prefs as UserPreferences);
          }
        }),
        catchError((err) => {
          console.warn('[UserPreferencesService] Failed to load preferences from backend:', err);
          return of({});
        }),
      );
  }

  /**
   * Persists the current local preferences state to the backend.
   * Fire-and-forget — errors are logged but not surfaced to the caller.
   * Call this any time a preference changes (layout config or tutorial completion).
   */
  saveToBackend(): void {
    const prefs = this.buildCurrentPreferences();
    this.http.put<{ ok: boolean }>(this.endpoint, prefs).subscribe({
      error: (err) =>
        console.warn('[UserPreferencesService] Failed to save preferences to backend:', err),
    });
  }
}
